/**
 * Nom et couleur de la marque d'un site Mindbody, pour Mon compte v2 (barres « Où ces cours ont eu
 * lieu », étiquettes de marque de l'historique, filtre par marque).
 *
 * Sources, dans l'ordre (SPEC § 5.2, aucune valeur propre à une marque dans le code) :
 * 1. `mondayMindbody.uxV2.brands` (nom et couleur lus dans les réglages de chaque marque), quand le
 *    serveur l'injecte ;
 * 2. le nom du site Mindbody (GET site/sites), raccourci (« dynamo - Monday Sports Club » →
 *    « dynamo ») ; couleur : celle du site courant pour lui-même (jeton --mmv2-brand), sinon aucune
 *    (l'appelant retombe sur l'encre).
 */
import type { MindbodySite } from 'types/site';
import { AA, ensureContrast, mix, normalizeHex, textOnColor } from '../../../lib/color';
import { absoluteHttpUrl } from '../../../lib/crossSite';
import { shortStudioName } from '../../../lib/planning/brands';

/** Forme injectée par le serveur (SPEC § 5.2) ; lue sans dépendre du type global. */
export interface InjectedBrand {
  siteId: number | string;
  name?: string;
  color?: string;
  textColor?: string;
  /** Page de réservation de la marque (planning multi-marques : cours ouverts dans un nouvel onglet). */
  url?: string;
  isCurrent?: boolean;
}

export interface BrandInfo {
  siteId: number;
  name: string;
  /** Couleur de marque (#rrggbb), absente si inconnue. */
  color?: string;
  /** Page de réservation de la marque (http(s) absolue), absente si inconnue. */
  url?: string;
  isCurrent: boolean;
}

/**
 * Étiquette de marque (Mon compte, Mes cours) : fond teinté de la couleur de la marque (12 % sur blanc), texte de la
 * couleur de la marque assombri juste assez pour 4,5:1 sur ce fond. null sans couleur connue (étiquette neutre).
 */
export function brandTagColors(color: string | undefined | null): { background: string; color: string } | null {
  const hex = normalizeHex(color);
  if (!hex) return null;
  const background = mix(hex, '#ffffff', 0.12);
  return { background, color: ensureContrast(hex, background, AA) };
}

/** Couleurs d'une carte de marque de « Mes chiffres » (toutes AA, lib/color.ts). */
export interface BrandCardPalette {
  /** Couleur de la marque telle quelle : liseré d'une carte sans cours, rappel des préférés. */
  accent: string;
  /** Fond d'une carte avec cours : la couleur, foncée au minimum dans sa teinte si son texte n'y atteint pas 4,5:1. */
  surface: string;
  /** Texte posé sur `surface` : blanc (convention des marques), encre sur une couleur claire. */
  on: string;
  /** Texte à la couleur de la marque sur le papier (nom et lien d'une carte sans cours) : 4,5:1 au moins. */
  text: string;
}

/**
 * Couleurs de la carte d'une marque (demande de Luc du 08/10 : « chacune des marques plus en avant avec des codes
 * couleurs différents »). `color` : couleur de la marque (uxV2.brands ; pour le site visité, son jeton --mmv2-brand,
 * comme le planning : un seul rouge sur la page) ; sans couleur connue, l'encre du site. Même règle que les boutons
 * de la v2 (textOnColor) : blanc sur une couleur qui le porte, surface foncée au minimum pour 4,5:1 ; encre sur une
 * couleur claire.
 */
export function brandCardPalette(
  color: string | undefined | null,
  site: { ink: string; paper: string },
): BrandCardPalette {
  const ink = normalizeHex(site.ink) ?? '#0b0c11';
  const paper = normalizeHex(site.paper) ?? '#ffffff';
  const accent = normalizeHex(color) ?? ink;
  const { surface, text: on } = textOnColor(accent, ink);
  return { accent, surface, on, text: ensureContrast(accent, paper, AA) };
}

/** Marques injectées par le serveur, [] si absentes. */
export function injectedBrands(): InjectedBrand[] {
  if (typeof mondayMindbody === 'undefined' || !mondayMindbody) return [];
  const brands = (mondayMindbody.uxV2 as { brands?: unknown } | undefined)?.brands;
  return Array.isArray(brands) ? (brands as InjectedBrand[]) : [];
}

/** « dynamo - Monday Sports Club » → « dynamo » ; « RIISE Studios » → « RIISE Studios ». */
export function shortSiteName(name: string | undefined | null): string {
  if (!name) return '';
  return name.split(' - ')[0].trim();
}

/** Marque d'un site. */
export function brandForSite(
  siteId: number | string,
  {
    sites,
    brands,
    currentSiteId,
  }: { sites?: MindbodySite[]; brands?: InjectedBrand[]; currentSiteId?: string | number },
): BrandInfo {
  const id = Number(siteId);
  const injected = (brands ?? []).find((b) => Number(b.siteId) === id);
  const site = sites?.find((s) => Number(s.Id) === id);
  const isCurrent = String(currentSiteId ?? '') === String(id);
  return {
    siteId: id,
    name: injected?.name || shortSiteName(site?.Name) || String(id),
    color: injected?.color || undefined,
    url: absoluteHttpUrl(injected?.url) ?? undefined,
    isCurrent,
  };
}

/**
 * Noms de marque connus, comme le planning (Planning/index.tsx `brandNames`) : marques injectées par le serveur
 * et « Nom Marque » du site. Les noms des sites Mindbody n'en font pas partie : « Monday Corporate » ferait de
 * « Monday Event » un studio « Event ».
 */
export function knownBrandNames({ brands, brandName }: { brands?: InjectedBrand[]; brandName?: string | null }): string[] {
  const names = [...(brands ?? []).map((b) => b.name ?? ''), brandName ?? ''];
  return [...new Set(names.map((name) => name.trim()).filter(Boolean))];
}

/** « dynamo Opéra » → « Opéra », « Punch - Louvre » → « Louvre » ; nom brut si aucun préfixe connu. */
export function studioDisplayName(locationName: string | undefined | null, brandNames: string[]): string {
  return shortStudioName((locationName ?? '').trim(), brandNames);
}
